<template>
    <div>
        <page-title title="Voorraad" icon="pe-7s-note2"/>

        <b-row class="mb-4">
            <b-col lg="6" class="mb-4 mb-lg-0">
                <b-card>
                    <b-card-title>ARTIKEL</b-card-title>

                    <p style="line-height: 20px;">
                        Via onderstaande zoekfunctie kunnen alle voorraadlocaties van een artikel opgevraagd worden.
                        <template v-if="canManageStock">
                            De voorraad kan aangepast worden.
                            Daarmee wordt direct de PBL 2.0 / Smart Logistics Engine database ge&uuml;pdatet. Let op: Deze voorraden kunnen (en zullen) overschreven worden vanuit de feitelijke
                            voorraadsystemen.
                        </template>
                    </p>

                    <form @submit.prevent="getStockLocationsForProduct">
                        <b-input style="width: 300px" type="text" placeholder="Vul een volledig artikelnummer in" v-model="searchProductNumber" class="d-inline-block mr-2" autofocus/>
                        <OmniButton type="submit"
                                    variant="primary"
                                    class="btn btn-xs"
                                    :disabled="isLoadingStockLocations"
                        >
                            <template v-if="!isLoadingStockLocations">
                                <span class="label">{{ $t('omt.gui.general.action.search') }}</span>
                            </template>

                            <template v-else>
                                <span class="label mr-2">{{ $t('omt.gui.general.action.search') }}&hellip;</span>
                                <span class="icon">
                                <font-awesome-icon icon="spinner" spin/>
                              </span>
                            </template>
                        </OmniButton>
                    </form>
                </b-card>

            </b-col>
            <b-col lg="6" v-if="showAdditionalProductInformation">
                <b-card v-if="loadedProductNumber" :key="loadedProductNumber">
                    <b-card-title>PRODUCT INFORMATIE</b-card-title>

                    <product-info :number="loadedProductNumber"/>
                </b-card>
            </b-col>
        </b-row>

        <b-card v-if="isStockLocationsLoaded && !isLoadingDispatchTypes">
            <b-card-title>VOORRAADLOCATIES VOOR PRODUCT: {{ loadedProductNumber }}</b-card-title>

            <b-table small :fields="stockTableFields" :items="stockLocations" striped responsive>
                <template #table-colgroup="scope">
                    <col style="width:100px;"/>
                    <col style="width:auto;"/>
                    <col v-if="showShipFromStoreIndicators" style="width:150px;"/>
                    <col style="width:150px;"/>
                    <col style="width:150px;"/>
                    <col style="width:150px;"/>
                </template>

                <template #cell(ship_from_store)="data">
                    <font-awesome-icon v-if="locationShipFromStoreEnabled[data.item.party_identifier]" icon="circle-check" class="text-success font-size-lg" title="Ja"/>
                    <font-awesome-icon v-else icon="circle-xmark" class="text-danger font-size-lg" title="Nee"/>
                </template>
                <template #cell(stock_level)="data">
                    <span v-if="editStockLocation === data.item.party_identifier">
                        <b-input v-model="newStock" type="number" min="0" :disabled="isSavingStockLocation" size="sm"/>
                    </span>
                    <span v-else>{{ data.item.stock_level }}</span>
                </template>
                <template #cell(stock_level_overrule)="data">
                    <span v-if="editStockLocation === data.item.party_identifier">
                        <b-input
                            :value="newStockOverrule"
                            @input="onOverruleQuantityInput('newStockOverrule', $event)"
                            type="number"
                            min="0"
                            :disabled="isSavingStockLocation"
                            size="sm"
                        />
                    </span>
                    <span v-else>{{ data.item.stock_level_overrule }}</span>
                </template>
                <template #cell(buttons)="data">
                    <OmniButton v-if="canManageStock && editStockLocation === null"
                                type="button"
                                size="sm"
                                variant="primary"
                                class="mr-1"
                                icon="pencil"
                                @click="editStock(data.item.party_identifier)"
                    >
                        Aanpassen
                    </OmniButton>

                    <OmniButton v-if="canManageStock && editStockLocation === data.item.party_identifier"
                                type="button"
                                size="sm"
                                variant="success"
                                icon="save"
                                class="mr-1"
                                @click="saveStockLocation"
                                :disabled="isSavingStockLocation"
                    >
                        Opslaan
                    </OmniButton>

                    <OmniButton v-if="canManageStock && editStockLocation === data.item.party_identifier"
                                type="button"
                                size="sm"
                                variant="danger"
                                icon="trash"
                                @click="cancelStock"
                                :disabled="isSavingStockLocation"
                    >
                        Annuleren
                    </OmniButton>
                </template>
            </b-table>

            <table style="width:100%;">
                <colgroup>
                    <col width="100px">
                    <col width="auto">
                    <col width="100px">
                    <col width="200px">
                </colgroup>
                <thead>
                <tr>
                    <th>Locatie ID</th>
                    <th>Locatie</th>
                    <th>Voorraad</th>
                    <th>Voorraad (overrule)</th>
                    <th></th>
                </tr>
                </thead>
                <tfoot></tfoot>

                <tbody>
                <tr>
                    <td>
                        <b-input v-if="canManageStock"
                                 type="number"
                                 v-model="newStockLocationPartyIdentifier"
                                 :disabled="isSavingStockLocation"
                                 size="sm"
                        />
                        <span v-else>
                                {{ newStockLocationPartyIdentifier }}
                            </span>
                    </td>
                    <td>
                        <i>Voeg voorraadlocatie toe</i>
                    </td>
                    <td>
                        <b-input v-if="canManageStock"
                                 type="number"
                                 v-model="newStockLocationQuantity"
                                 min="0"
                                 :disabled="isSavingStockLocation"
                                 size="sm"
                        />
                        <span v-else>
                                {{ newStockLocationQuantity }}
                            </span>
                    </td>
                    <td>
                        <b-input v-if="canManageStock"
                                 :value="newStockLocationQuantityOverrule"
                                 @input="onOverruleQuantityInput('newStockLocationQuantityOverrule', $event)"
                                 type="number"
                                 min="0"
                                 :disabled="isSavingStockLocation"
                                 size="sm"
                        />
                        <span v-else>
                                {{ newStockLocationQuantityOverrule }}
                            </span>
                    </td>
                    <td>
                        <OmniButton v-if="canManageStock"
                                    type="button"
                                    size="sm"
                                    variant="primary"
                                    @click="saveNewStockLocation"
                                    :disabled="isSavingStockLocation || !isNewStockLocationValid"
                                    class="btn btn-xs"
                                    icon="circle-plus"
                        >
                            Toevoegen
                        </OmniButton>

                        <span v-if="isNewStockLocationAlreadyInList">
                                Locatie ID staat al in de lijst!
                            </span>
                    </td>
                </tr>
                </tbody>
            </table>
        </b-card>
        <b-card v-else-if="loadedProductNumber && !isLoadingStockLocations">
            <b-card-title>VOORRAADLOCATIES VOOR PRODUCT: {{ loadedProductNumber }}</b-card-title>
            <div class="alert alert-warning">
                Product niet gevonden
            </div>
        </b-card>
    </div>
</template>

<script>
import PageTitle from "../../components/PageTitle.vue";
import ProductInfo from "../../components/ProductInfo.vue";

export default {
    components: {ProductInfo, PageTitle},

    data() {
        return {
            isLoadingStockLocations: false,
            isStockLocationsLoaded: false,
            isSavingStockLocation: false,
            isLoadingDispatchTypes: true,
            searchProductNumber: '',
            loadedProductNumber: '',
            stockLocations: [],
            locationShipFromStoreEnabled: {},
            editStockLocation: null,
            newStock: 0,
            newStockOverrule: null,
            newStockLocationPartyIdentifier: null,
            newStockLocationQuantity: null,
            newStockLocationQuantityOverrule: null,
        }
    },

    mounted() {
        this.$store.getters.getDispatchTypes
            .then(() => this.isLoadingDispatchTypes = false)

        if (this.$route.params.productIdentifier) {
            this.loadedProductNumber = this.searchProductNumber = this.$route.params.productIdentifier
            this.getStockLocationsForProduct()
        }
    },

    computed: {
        stockTableFields() {
            return [
                {
                    key: 'party_identifier',
                    label: 'Locatie ID',
                    sortable: true,
                },
                {
                    key: 'party_name',
                    label: 'Locatie',
                    sortable: true,
                },
                ...((this.showShipFromStoreIndicators)
                        ? [                {
                            key: 'ship_from_store',
                            label: 'Ship From Store',
                        },]
                        : []
                ),
                {
                    key: 'stock_level',
                    label: 'Voorraad',
                    sortable: true,
                },
                {
                    key: 'stock_level_overrule',
                    label: 'Voorraad (overrule)',
                    sortable: true,
                },
                {
                    key: 'buttons',
                    label: '',
                },
            ]
        },
        isNewStockLocationValid() {
            return (
                this.newStockLocationPartyIdentifier
                && this.newStockLocationQuantity
                && !this.isNewStockLocationAlreadyInList
            )
        },
        isNewStockLocationAlreadyInList() {
            return !!this.stockLocations.filter(stockLocation => stockLocation.party_identifier === this.newStockLocationPartyIdentifier).length
        },
        canManageStock() {
            return this.hasPermission('stockmanagement.edit');
        },
        currentEditStockLocation() {
            return this.stockLocations.find(l => l.party_identifier === this.editStockLocation)
        },
        showShipFromStoreIndicators() {
          return this.$store.state.dispatchTypes?.['ship_from_store']?.is_active
              && this.$store.getters.getFrontendParameterValue('stock-management', 'show-ship-from-store-indicators');
        },
        showAdditionalProductInformation() {
          return this.$store.getters.getFrontendParameterValue('stock-management', 'show-additional-product-information');
        }
    },

    methods: {
        getStockLocationsForProduct() {
            this.isLoadingStockLocations = true
            this.editStockLocation = null
            this.isStockLocationsLoaded = false
            this.stockLocations = []
            this.loadedProductNumber = this.searchProductNumber.trim() // Make sure the correct productnr is sent in api calls

            this.$router.replace({name: 'StockManagementWithProduct', params: {productIdentifier: this.loadedProductNumber}})

            this.$http.get('/api/proxy/sle/api/stock/products/' + this.loadedProductNumber)
                .then(response => {
                    this.stockLocations = response.data

                    this.getShipFromStoreInfoForLocations()
                        .finally(() => {
                            this.isLoadingStockLocations = false
                            this.isStockLocationsLoaded = true
                        })
                })
                .catch(() => {
                })
                .then(() => {
                })
        },
        onOverruleQuantityInput(variable, value) {
            this[variable] = value === '' || value === undefined ? null : Number(value)
        },
        saveStockLocation() {
            this.isSavingStockLocation = true
            this.$http.put('/api/proxy/sle/api/stock/products/' + this.loadedProductNumber + '/party/' + this.currentEditStockLocation.party_identifier, {
                stock_level: parseInt(this.newStock),
                delta: false,
                overrule: false,
            })
                .then(response => {
                    this.currentEditStockLocation.stock_level = response.data.stock_level
                    this.$alert('Voorraad opgeslagen')
                })
                .catch((error) => {
                    this.$alert("Fout bij opslaan: " + error.message, 'danger')
                })
                .then(() => {
                })

            this.$http.put('/api/proxy/sle/api/stock/products/' + this.loadedProductNumber + '/party/' + this.currentEditStockLocation.party_identifier, {
                stock_level: this.newStockOverrule,
                delta: false,
                overrule: true,
            })
                .then(response => {
                    this.currentEditStockLocation.stock_level_overrule = response.data.stock_level_overrule
                    this.$alert('Voorraad-overrule opgeslagen')
                })
                .catch((error) => {
                    this.$alert("Fout bij opslaan: " + error.message, 'danger')
                })
                .then(() => {
                    this.isSavingStockLocation = false
                })
        },

        editStock(identifier, overrule) {
            this.editStockLocation = identifier

            this.newStock = this.currentEditStockLocation.stock_level
            this.newStockOverrule = this.currentEditStockLocation.stock_level_overrule
        },
        cancelStock() {
            this.editStockLocation = null
        },
        saveNewStockLocation() {
            this.isSavingStockLocation = true
            this.$http.get('/api/proxy/sle/api/parties/' + this.newStockLocationPartyIdentifier)
                .then(partyResponse => {
                    // Do the save
                    this.$http.put('/api/proxy/sle/api/stock/products/' + this.loadedProductNumber + '/party/' + this.newStockLocationPartyIdentifier, {
                        stock_level: parseInt(this.newStockLocationQuantity),
                        delta: false,
                        overrule: false,
                    })
                        .then(stockResponse => {
                            this.stockLocations.push({
                                party_identifier: stockResponse.data.party_identifier,
                                party_name: partyResponse.data.name,
                                product_identifier: stockResponse.data.product_identifier,
                                stock_level: stockResponse.data.stock_level,
                                stock_level_overrule: stockResponse.data.stock_level_overrule,
                            })
                            this.newStockLocationPartyIdentifier = null
                            this.newStockLocationQuantity = null

                            this.$alert('Voorraad opgeslagen')
                        })
                        .catch((error) => {
                            this.$alert("Fout bij opslaan: " + error.message, 'danger')
                        })
                        .then(() => {
                            this.isSavingStockLocation = false
                        })
                })
                .catch(() => {
                    this.$alert("Geen locatie met identifier '" + this.newStockLocationPartyIdentifier + "' gevonden!", 'danger')
                    this.isSavingStockLocation = false
                })
                .then(() => {
                })

            if (this.newStockLocationQuantityOverrule) {
                this.$http.get('/api/proxy/sle/api/parties/' + this.newStockLocationPartyIdentifier)
                    .then(partyResponse => {
                        // Do the save
                        this.$http.put('/api/proxy/sle/api/stock/products/' + this.loadedProductNumber + '/party/' + this.newStockLocationPartyIdentifier, {
                            stock_level: parseInt(this.newStockLocationQuantityOverrule),
                            delta: false,
                            overrule: true,
                        })
                            .then(stockResponse => {
                                this.newStockLocationPartyIdentifier = null
                                this.newStockLocationQuantityOverrule = null

                                this.$alert('Voorraad-overrule opgeslagen')
                            })
                            .catch((error) => {
                                this.$alert("Fout bij opslaan: " + error.message, 'danger')
                            })
                            .then(() => {
                                this.isSavingStockLocation = false
                            })
                    })
                    .catch(() => {
                        this.$alert("Geen locatie met identifier '" + this.newStockLocationPartyIdentifier + "' gevonden!", 'danger')
                        this.isSavingStockLocation = false
                    })
                    .then(() => {
                    })
            }
        },

        async getShipFromStoreInfoForLocations() {
            return this.$http.post(`/api/rest/location-property-keys/services.ship_from_store.enabled/values`, {
                params: {
                    location_identifiers: this.stockLocations.map(sl => sl.party_identifier),
                }
            })
                .then(response => {
                    response.data.forEach(value => {
                        this.locationShipFromStoreEnabled[value.location.identifier] = value.value
                    })
                })
        },
    },
}
</script>

<style>
table {
    width: 100%;
    font-size: .8rem;
}

table td {
    padding: .25rem 1rem .25rem 0;
}

table td:last-child {
    padding-right: 0;
}
</style>
